import React, { useState } from 'react';
import { User, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NicknameModal = ({ isOpen, onClose, onNicknameSet, user }) => {
  const [nickname, setNickname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset nickname input when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setNickname(user?.nickname || '');
      setError('');
    }
  }, [isOpen, user?.nickname]);

  if (!isOpen) return null;

  const isChangingNickname = !!user?.nickname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const trimmedNickname = nickname.trim();
    
    if (trimmedNickname.length < 2) {
      setError('Nickname must be at least 2 characters');
      return;
    }
    
    if (trimmedNickname.length > 30) {
      setError('Nickname must be 30 characters or less');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/auth/nickname`,
        { nickname: trimmedNickname },
        { withCredentials: true }
      );
      
      toast.success('Nickname set successfully!');
      onNicknameSet(response.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to set nickname';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="nickname-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={isChangingNickname ? onClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Close button for changing nickname */}
        {isChangingNickname && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors"
            data-testid="nickname-modal-close"
          >
            <X className="h-6 w-6" />
          </button>
        )}
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#001f3f] to-[#003366] p-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-[#DC2626] p-3 rounded-full">
              <Sparkles className="h-8 w-8" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center uppercase tracking-wider">
            {isChangingNickname ? 'Change Your Nickname' : 'Welcome to the Community!'}
          </h2>
          <p className="text-center text-slate-300 mt-2">
            {isChangingNickname 
              ? 'Enter a new nickname to use in discussions'
              : 'Choose a nickname to use in discussions'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Your Nickname
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value);
                  setError('');
                }}
                placeholder="Enter a unique nickname..."
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-[#DC2626] focus:border-transparent transition-all"
                maxLength={30}
                autoFocus
                data-testid="nickname-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              2-30 characters. This will be shown instead of your real name.
            </p>
            {error && (
              <p className="text-sm text-red-600 mt-2" data-testid="nickname-error">
                {error}
              </p>
            )}
          </div>

          {/* User Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Logged in as</p>
            <div className="flex items-center space-x-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#001f3f] flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
              )}
              <div>
                <p className="font-semibold text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !nickname.trim() || (isChangingNickname && nickname.trim() === user?.nickname)}
            className="w-full bg-[#DC2626] hover:bg-[#b91c1c] text-white py-3 rounded-full font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="nickname-submit"
          >
            {submitting ? 'Saving...' : (isChangingNickname ? 'Update Nickname' : 'Set My Nickname')}
          </button>

          {isChangingNickname && (
            <button
              type="button"
              onClick={onClose}
              className="w-full mt-2 text-slate-500 hover:text-slate-700 py-2 font-medium transition-colors"
              data-testid="nickname-cancel"
            >
              Cancel
            </button>
          )}

          <p className="text-xs text-slate-500 text-center mt-4">
            {isChangingNickname 
              ? 'Your new nickname will appear in all your future posts.'
              : 'You can change your nickname later in your profile settings.'}
          </p>
        </form>
      </div>
    </div>
  );
};

export default NicknameModal;
